import React, { useMemo } from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, 
  Line, AreaChart, Area
} from "recharts";
import { 
  Truck, Users, Package, AlertTriangle, TrendingUp, 
  Calendar, DollarSign, MapPin, Activity, Shield,
  Clock, Zap, FileText, Route
} from "lucide-react";

// Import your actual API hooks
import { useGetAllUsersQuery } from "../../redux/admin/dashboardApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { 
  useGetTripsQuery, 
  useGetExpensesQuery, 
  useGetMaterialsQuery,
  useGetLicensesQuery,
  useGetRoutesQuery,
  useGetSensorsQuery,
  useGetRemindersQuery,
  useGetDocumentsQuery
} from "../../redux/logistics/logisticsApi";
import { useGetAllEventsQuery, useGetAllAssetsQuery } from "../../redux/admin/adminApi";

// Helper for safe array
const safeArray = (res) => (Array.isArray(res) ? res : res?.data || []);

// Helper Component for Loading State (Skeleton)
const LoadingSkeleton = ({ height = "200px" }) => (
  <div style={{ 
    height, 
    width: "100%", 
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "pulse 1.5s infinite",
    borderRadius: "8px",
    opacity: 0.7
  }} />
);

const FleetDashboard = () => {
  // Fetch all data (Parallel Fetching)
  const { data: usersRes, isLoading: loadingUsers } = useGetAllUsersQuery();
  const { data: devicesRes, isLoading: loadingDevices } = useGetAllLiteDevicesQuery();
  const { data: tripsRes, isLoading: loadingTrips } = useGetTripsQuery();
  const { data: expensesRes, isLoading: loadingExpenses } = useGetExpensesQuery();
  const { data: materialsRes, isLoading: loadingMaterials } = useGetMaterialsQuery();
  const { data: licensesRes, isLoading: loadingLicenses } = useGetLicensesQuery();
  const { data: routesRes, isLoading: loadingRoutes } = useGetRoutesQuery();
  const { data: sensorsRes, isLoading: loadingSensors } = useGetSensorsQuery();
  const { data: remindersRes, isLoading: loadingReminders } = useGetRemindersQuery();
  const { data: documentsRes, isLoading: loadingDocuments } = useGetDocumentsQuery();
  const { data: eventsRes, isLoading: loadingEvents } = useGetAllEventsQuery();
  const { data: assetsRes, isLoading: loadingAssets } = useGetAllAssetsQuery();

  // Process Data safely
  const users = safeArray(usersRes);
  const devices = safeArray(devicesRes);
  const trips = safeArray(tripsRes);
  const expenses = safeArray(expensesRes);
  const materials = safeArray(materialsRes);
  const licenses = safeArray(licensesRes);
  const routes = safeArray(routesRes);
  const sensors = safeArray(sensorsRes);
  const reminders = safeArray(remindersRes);
  const documents = safeArray(documentsRes);
  const events = safeArray(eventsRes);
  const assets = safeArray(assetsRes);

  // Group Loadings for specific sections
  const kpiLoading = loadingDevices || loadingTrips || loadingExpenses || loadingUsers;

  // KPI Calculation
  const kpiData = useMemo(() => {
    const activeDevices = devices.filter(d => d.status === 'online' || d.status === 'active').length;
const completedTrips = trips.filter(t => 
  ["completed", "Completed", "Scheduled", "In-Progress"].includes(t.status)
).length;
    const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const activeUsers = users.filter(u => u.isActive && !u.isDeleted).length;
    
    return [
      { title: "Active Vehicles", value: activeDevices, total: devices.length, icon: Truck, color: "kpi-blue", change: "+12%" },
      { title: "Total Trips", value: completedTrips, total: trips.length, icon: Route, color: "kpi-green", change: "+8%" },
      { title: "Active Users", value: activeUsers, total: users.length, icon: Users, color: "kpi-purple", change: "+5%" },
      { title: "Monthly Expenses", value: `₹${(totalExpenses / 1000).toFixed(1)}K`, icon: DollarSign, color: "kpi-orange", change: "-3%" }
    ];
  }, [devices, trips, expenses, users]);

  // Chart Calculations
  const fleetStatusData = useMemo(() => {
    const statusCount = {};
    devices.forEach(d => {
      const status = d.status || 'unknown';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    const colors = { online: "#10B981", active: "#3B82F6", offline: "#F59E0B", expired: "#EF4444", unknown: "#6B7280" };
    return Object.keys(statusCount).map(key => ({ name: key.charAt(0).toUpperCase() + key.slice(1), value: statusCount[key], color: colors[key] || "#6B7280" }));
  }, [devices]);

  const tripAnalytics = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      const dayTrips = trips.filter(t => new Date(t.createdAt).toDateString() === date.toDateString());
      return {
        name: date.toLocaleDateString('en-US', { weekday: 'short' }),
        trips: dayTrips.length,
        distance: dayTrips.reduce((sum, t) => sum + (t.distance || 0), 0)
      };
    });
  }, [trips]);

  const expenseBreakdown = useMemo(() => {
    const categories = {};
    expenses.forEach(e => {
      const cat = e.category || 'Other';
      if (!categories[cat]) categories[cat] = { name: cat, amount: 0 };
      categories[cat].amount += (e.amount || 0);
    });
    return Object.values(categories);
  }, [expenses]);

  const deviceTypeData = useMemo(() => {
    const types = {};
    devices.forEach(d => {
      const type = d.deviceType || d.model || "Unknown";
      if (!types[type]) types[type] = { name: type, total: 0, active: 0 };
      types[type].total++;
      if (d.status === 'online' || d.status === 'active') types[type].active++;
    });
    return Object.values(types);
  }, [devices]);

  const materialStock = useMemo(() => {
    const statusCount = {};
    materials.forEach(m => {
      const status = m.status || 'unknown';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    const colors = { 'in-stock': "#10B981", 'low-stock': "#F59E0B", 'out-of-stock': "#EF4444" };
    return Object.keys(statusCount).map(key => ({
      name: key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      value: statusCount[key],
      color: colors[key] || "#6B7280"
    }));
  }, [materials]);

  const userRoleData = useMemo(() => {
    const roles = {};
    users.forEach(u => {
      const role = u.role || 'unknown';
      roles[role] = (roles[role] || 0) + 1;
    });
    return Object.keys(roles).map(key => ({
      name: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      value: roles[key]
    }));
  }, [users]);


  // ✅ Asset Status Chart Data
const assetStatus = useMemo(() => {
  const statusCount = {};
  assets.forEach(a => {
    const status = a.status || "unknown";
    statusCount[status] = (statusCount[status] || 0) + 1;
  });

  const colors = {
    active: "#10B981",
    inactive: "#EF4444",
    maintenance: "#F59E0B",
    expired: "#8B5CF6",
    unknown: "#6B7280"
  };

  return Object.keys(statusCount).map(key => ({
    name: key.toUpperCase(),
    value: statusCount[key],
    color: colors[key] || "#6B7280"
  }));
}, [assets]);

// ✅ License Status Chart Data
const licenseStatus = useMemo(() => {
  const statusCount = {};
  licenses.forEach(l => {
    const status = l.status || "unknown";
    statusCount[status] = (statusCount[status] || 0) + 1;
  });

  const colors = {
    active: "#10B981",
    expired: "#EF4444",
    pending: "#F59E0B",
    unknown: "#6B7280"
  };

  return Object.keys(statusCount).map(key => ({
    name: key.toUpperCase(),
    value: statusCount[key],
    color: colors[key] || "#6B7280"
  }));
}, [licenses]);


  // REMOVED: Global isLoading check. Now we render immediately.

  return (
    <>
      <style>{`
        @keyframes pulse { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        /* ... (Your existing styles remain same, pasting relevant ones for context) ... */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif; background: #f9fafb; }
        .dashboard-container { min-height: 100vh; background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%); padding: 1rem; }
        @media (min-width: 640px) { .dashboard-container { padding: 1.5rem; } }
        .dashboard-header { margin-bottom: 2rem; }
        .dashboard-title { font-size: 1.875rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; }
        .dashboard-subtitle { color: #6b7280; font-size: 0.875rem; }
        .kpi-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; margin-bottom: 1.5rem; }
        @media (min-width: 640px) { .kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) { .kpi-grid { grid-template-columns: repeat(4, 1fr); } }
        .kpi-card { border-radius: 1rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); padding: 1.5rem; color: white; position: relative; overflow: hidden; }
        .kpi-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .kpi-green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .kpi-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
        .kpi-orange { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .kpi-overlay { position: absolute; top: 0; right: 0; width: 5rem; height: 5rem; background: rgba(255, 255, 255, 0.1); border-radius: 50%; margin-right: -2.5rem; margin-top: -2.5rem; }
        .kpi-content { position: relative; z-index: 10; }
        .kpi-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 0.75rem; }
        .kpi-label { color: rgba(255, 255, 255, 0.9); font-size: 0.875rem; font-weight: 500; margin-bottom: 0.25rem; }
        .kpi-value { font-size: 2rem; font-weight: 700; margin-bottom: 0.25rem; }
        .kpi-total { color: rgba(255, 255, 255, 0.8); font-size: 0.75rem; }
        .kpi-icon { width: 2.5rem; height: 2.5rem; opacity: 0.8; }
        .kpi-footer { display: flex; align-items: center; font-size: 0.875rem; margin-top: 0.75rem; }
        .kpi-change { font-weight: 500; }
        .chart-grid, .chart-grid-2 { display: grid; grid-template-columns: 1fr; gap: 1rem; margin-bottom: 1rem; }
        @media (min-width: 1024px) { .chart-grid { grid-template-columns: repeat(3, 1fr); } .chart-grid-2 { grid-template-columns: repeat(2, 1fr); } }
        .card { background: white; border-radius: 1rem; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1); padding: 1.5rem; }
        .card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; }
        .card-title { font-size: 1.125rem; font-weight: 600; color: #1f2937; }
        .card-icon { width: 1.25rem; height: 1.25rem; color: #6b7280; }
        .table-container { overflow-x: auto; margin-top: 1rem; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table thead { background: #f9fafb; }
        .data-table th { padding: 0.75rem; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb; }
        .data-table td { padding: 0.75rem; border-bottom: 1px solid #f3f4f6; color: #6b7280; }
        .status-badge { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; }
        .status-active { background: #d1fae5; color: #065f46; }
        .status-inactive { background: #fee2e2; color: #991b1b; }
        .status-pending { background: #fef3c7; color: #92400e; }
        .alert-card { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); border-radius: 1rem; padding: 1.5rem; color: white; }
        .alert-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
        .alert-item { background: rgba(255, 255, 255, 0.2); border-radius: 0.5rem; padding: 0.75rem; margin-bottom: 0.75rem; backdrop-filter: blur(10px); }
        .grid-3 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
        @media (min-width: 640px) { .grid-3 { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) { .grid-3 { grid-template-columns: repeat(3, 1fr); } }
      `}</style>

      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Fleet Management Dashboard</h1>
          <p className="dashboard-subtitle">Real-time operations overview and analytics</p>
        </div>

        {/* KPI Cards (Optimized with individual loaders) */}
        <div className="kpi-grid">
          {kpiLoading ? (
            // Show 4 skeletons while KPI data loads
            [...Array(4)].map((_, i) => <LoadingSkeleton key={i} height="150px" />)
          ) : (
            kpiData.map((kpi, idx) => {
              const Icon = kpi.icon;
              return (
                <div key={idx} className={`kpi-card ${kpi.color}`}>
                  <div className="kpi-overlay"></div>
                  <div className="kpi-content">
                    <div className="kpi-header">
                      <div>
                        <p className="kpi-label">{kpi.title}</p>
                        <h3 className="kpi-value">{kpi.value}</h3>
                        {kpi.total && <p className="kpi-total">of {kpi.total} total</p>}
                      </div>
                      <Icon className="kpi-icon" />
                    </div>
                    <div className="kpi-footer">
                      <TrendingUp size={16} className="mr-1" />
                      <span className="kpi-change">{kpi.change}</span>
                      <span className="kpi-period">vs last month</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Fleet Status, Device Types, Material Stock */}
        <div className="chart-grid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Fleet Status</h3>
              <Truck className="card-icon" />
            </div>
            {loadingDevices ? <LoadingSkeleton height="220px" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={fleetStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {fleetStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Device Types</h3>
              <Package className="card-icon" />
            </div>
            {loadingDevices ? <LoadingSkeleton height="220px" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={deviceTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#3B82F6" radius={[8, 8, 0, 0]} name="Total" />
                  <Bar dataKey="active" fill="#10B981" radius={[8, 8, 0, 0]} name="Active" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Material Stock</h3>
              <Package className="card-icon" />
            </div>
            {loadingMaterials ? <LoadingSkeleton height="220px" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={materialStock} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} label>
                    {materialStock.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Trip Analytics & Expenses */}
        <div className="chart-grid-2">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Trip Analytics (Last 7 Days)</h3>
              <MapPin className="card-icon" />
            </div>
            {loadingTrips ? <LoadingSkeleton height="250px" /> : (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={tripAnalytics}>
                  <defs>
                    <linearGradient id="colorTrips" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="trips" stroke="#3B82F6" fillOpacity={1} fill="url(#colorTrips)" name="Trips" />
                  <Area type="monotone" dataKey="distance" stroke="#10B981" fillOpacity={0.6} fill="#10B981" name="Distance (km)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Expense Breakdown</h3>
              <DollarSign className="card-icon" />
            </div>
            {loadingExpenses ? <LoadingSkeleton height="250px" /> : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={expenseBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="amount" fill="#F59E0B" radius={[0, 8, 8, 0]} name="Amount" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Tables Section: Trips & Expenses */}
        <div className="chart-grid-2">
          {/* Recent Trips Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Trips</h3>
              <Route className="card-icon" />
            </div>
            {loadingTrips ? <LoadingSkeleton height="200px" /> : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>ID</th><th>Status</th><th>Distance</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {trips.slice(0, 5).map((trip, idx) => (
                      <tr key={trip._id || idx}>
                        <td>#{String(idx + 1).padStart(3, '0')}</td>
                        <td>
                          <span className={`status-badge ${trip.status === 'completed' ? 'status-active' : trip.status === 'in-progress' ? 'status-pending' : 'status-inactive'}`}>
                            {trip.status || 'N/A'}
                          </span>
                        </td>
                        <td>{trip.distance || 0} km</td>
                        <td>{new Date(trip.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Expenses Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Expenses</h3>
              <DollarSign className="card-icon" />
            </div>
            {loadingExpenses ? <LoadingSkeleton height="200px" /> : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>Category</th><th>Amount</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {expenses.slice(0, 5).map((expense, idx) => (
                      <tr key={expense._id || idx}>
                        <td>{expense.category || 'N/A'}</td>
                        <td>₹{(expense.amount || 0).toLocaleString()}</td>
                        <td>{new Date(expense.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* More Charts */}
        <div className="chart-grid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">User Roles</h3>
              <Users className="card-icon" />
            </div>
            {loadingUsers ? <LoadingSkeleton height="220px" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={userRoleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {userRoleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'][index % 5]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Asset Status</h3>
              <Package className="card-icon" />
            </div>
            {loadingAssets ? <LoadingSkeleton height="220px" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={assetStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {assetStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">License Status</h3>
              <FileText className="card-icon" />
            </div>
            {loadingLicenses ? <LoadingSkeleton height="220px" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={licenseStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {licenseStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Route Efficiency & Events */}
        <div className="chart-grid-2">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Route Efficiency</h3>
              <Route className="card-icon" />
            </div>
            {loadingRoutes ? <LoadingSkeleton height="280px" /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={routes.slice(0, 8).map((r, i) => ({ name: r.name || `Route ${i + 1}`, efficiency: r.efficiency || Math.floor(Math.random() * 40) + 60 }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="efficiency" radius={[8, 8, 0, 0]}>
                    {(routes.slice(0, 8)).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={(entry.efficiency || 0) > 80 ? '#10B981' : (entry.efficiency || 0) > 60 ? '#F59E0B' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Events Timeline</h3>
              <Activity className="card-icon" />
            </div>
            {loadingEvents ? <LoadingSkeleton height="280px" /> : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={Array.from({ length: 7 }, (_, i) => {
                    const date = new Date(); date.setDate(date.getDate() - (6 - i));
                    return { name: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), events: events.filter(e => new Date(e.createdAt || e.timestamp).toDateString() === date.toDateString()).length };
                  })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="events" stroke="#8B5CF6" strokeWidth={2} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* More Tables Section (Optimized) */}
        <div className="chart-grid-2">
          {/* Active Devices Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Active Devices</h3>
              <Truck className="card-icon" />
            </div>
            {loadingDevices ? <LoadingSkeleton height="200px" /> : (
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Device</th><th>Type</th><th>Status</th><th>Assigned</th></tr></thead>
                  <tbody>
                    {devices.slice(0, 5).map((device, idx) => (
                      <tr key={device._id || idx}>
                        <td>{device.name || `Device ${idx + 1}`}</td>
                        <td>{device.deviceType || 'N/A'}</td>
                        <td><span className={`status-badge ${device.status === 'online' || device.status === 'active' ? 'status-active' : 'status-inactive'}`}>{device.status || 'N/A'}</span></td>
                        <td>{device.assignedToUser ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Materials Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Materials Inventory</h3>
              <Package className="card-icon" />
            </div>
            {loadingMaterials ? <LoadingSkeleton height="200px" /> : (
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Material</th><th>Quantity</th><th>Status</th></tr></thead>
                  <tbody>
                    {materials.slice(0, 5).map((material, idx) => (
                      <tr key={material._id || idx}>
                        <td>{material.name || `Material ${idx + 1}`}</td>
                        <td>{material.quantity || 0}</td>
                        <td><span className={`status-badge ${material.status === 'in-stock' ? 'status-active' : material.status === 'low-stock' ? 'status-pending' : 'status-inactive'}`}>{material.status || 'N/A'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sensor Health & Alerts */}
        <div className="grid-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Sensor Health</h3>
              <Zap className="card-icon" />
            </div>
            {loadingSensors ? <LoadingSkeleton height="200px" /> : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { name: "Active", value: sensors.filter(s => s.status === 'active').length, color: "#10B981" },
                  { name: "Inactive", value: sensors.filter(s => s.status !== 'active').length, color: "#EF4444" }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    <Cell fill="#10B981" />
                    <Cell fill="#EF4444" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Licenses Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Licenses</h3>
              <FileText className="card-icon" />
            </div>
            {loadingLicenses ? <LoadingSkeleton height="200px" /> : (
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>ID</th><th>Expiry</th><th>Status</th></tr></thead>
                  <tbody>
                    {licenses.slice(0, 4).map((license, idx) => (
                      <tr key={license._id || idx}>
                        <td>#{String(idx + 1).padStart(3, '0')}</td>
                        <td>{new Date(license.expiryDate).toLocaleDateString()}</td>
                        <td><span className={`status-badge ${license.status === 'active' ? 'status-active' : 'status-inactive'}`}>{license.status || 'N/A'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Alerts Card */}
          <div className="alert-card">
            <div className="alert-header">
              <h3 className="alert-title">Alerts & Warnings</h3>
              <AlertTriangle className="alert-icon" />
            </div>
            {(loadingLicenses || loadingMaterials || loadingSensors) ? <LoadingSkeleton height="150px" /> : (
              <div className="alert-list">
                <div className="alert-item">
                  <div className="alert-item-header"><Clock /><span className="alert-item-title">License Expiring</span></div>
                  <p className="alert-item-text">{licenses.filter(l => { const d = Math.floor((new Date(l.expiryDate) - new Date()) / (86400000)); return d <= 30 && d > 0; }).length} licenses expiring in 30 days</p>
                </div>
                <div className="alert-item">
                  <div className="alert-item-header"><AlertTriangle /><span className="alert-item-title">Low Stock Alert</span></div>
                  <p className="alert-item-text">{materials.filter(m => (m.status || '').includes('low')).length} materials need restocking</p>
                </div>
                <div className="alert-item">
                  <div className="alert-item-header"><Shield /><span className="alert-item-title">Inactive Sensors</span></div>
                  <p className="alert-item-text">{sensors.filter(s => s.status !== 'active').length} sensors require attention</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FleetDashboard;
